import React from 'react';
import PromptEngineeringCard from '@/components/cards/homepage/PromptEngineeringCard';
import AIToolsAcceleratorCard from '@/components/cards/homepage/AIToolsAcceleratorCard';
import { useFeaturedPricing } from '@/hooks/useFeaturedPricing';

/**
 * LearningTracksSection Component
 *
 * Displays the essential learning tracks section with featured courses.
 * Uses reusable card components for consistent design and maintainability.
 *
 * Pricing is dynamically fetched from the backend pricing API to ensure
 * consistency with the admin panel settings.
 *
 * @component
 * @example
 * ```tsx
 * <LearningTracksSection />
 * ```
 */
export default function LearningTracksSection() {
  // Fetch pricing for all featured products in a single API call
  const { promptEngineeringPricing, aiToolsPricing, loading } = useFeaturedPricing();

  return (
    <section className="py-24 px-6 bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-black to-blue-900/10"></div>
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
      }}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent font-semibold">
              🚀 ESSENTIAL LEARNING TRACKS
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="block">Start Your AI Journey</span>
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              With Our Core Courses
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Master the fundamentals that will 10x your productivity and unlock unlimited possibilities
          </p>
        </div>

        {/* Course Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <PromptEngineeringCard pricing={promptEngineeringPricing} loading={loading} />
          <AIToolsAcceleratorCard pricing={aiToolsPricing} loading={loading} />
        </div>
      </div>
    </section>
  );
}
